import { useState } from 'react'
import { useApp } from '../context/AppContext'
import Modal from '../components/Shared/Modal'
import useModal from '../hooks/useModal'

const MyAccount = () => {
  const { user, supabase } = useApp()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')

  // Estados para mostrar/ocultar contraseña
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const { isOpen, modalConfig, isLargeModal, openModal, closeModal } =
    useModal()

  const handlePasswordChange = async (e) => {
    e.preventDefault()
    setPasswordError('')
    setPasswordSuccess('')

    const hasUpperCase = /[A-Z]/.test(newPassword)
    const hasLowerCase = /[a-z]/.test(newPassword)
    const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)

    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden.')
      return
    }

    if (newPassword.length < 8) {
      setPasswordError('La contraseña debe tener al menos 8 caracteres.')
      return
    }

    if (!hasUpperCase || !hasLowerCase || !hasSymbol) {
      setPasswordError('Debe incluir mayúsculas, minúsculas y un símbolo.')
      return
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      })
      if (error) throw error

      setPasswordSuccess('Contraseña actualizada exitosamente.')
      setNewPassword('')
      setConfirmPassword('')

      setTimeout(() => {
        closeModal()
        setPasswordSuccess('')
      }, 2000)
    } catch (error) {
      setPasswordError(error.message)
    }
  }

  const openPasswordModal = () => {
    openModal('Cambiar Contraseña', null)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getInitials = (fullName) => {
    if (!fullName) return 'U'
    return fullName
      .split(' ')
      .map((name) => name[0])
      .join('')
      .toUpperCase()
  }

  if (!user) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning" role="alert">
          No hay datos de usuario disponibles. Por favor, inicia sesión.
        </div>
      </div>
    )
  }

  return (
    <div className="container mt-5">
      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        title={modalConfig.title}
        isLarge={isLargeModal}
      >
        <form onSubmit={handlePasswordChange}>
          <div className="mb-3">
            <label htmlFor="newPassword" className="form-label">
              Nueva Contraseña
            </label>
            <div className="input-group">
              <input
                type={showNewPassword ? 'text' : 'password'}
                className="form-control"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="confirmPassword" className="form-label">
              Confirmar Nueva Contraseña
            </label>
            <div className="input-group">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                className="form-control"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
          </div>

          {passwordError && (
            <div className="alert alert-danger">{passwordError}</div>
          )}
          {passwordSuccess && (
            <div className="alert alert-success">{passwordSuccess}</div>
          )}
          <button type="submit" className="btn btn-primary w-100">
            Cambiar Contraseña
          </button>
        </form>
      </Modal>

      <div className="row">
        <div className="col-12">
          <h1 className="mb-4">Mi Cuenta</h1>
        </div>
      </div>

      {/* Profile Header */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-auto">
              <div
                className="avatar-circle"
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--brand)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                }}
              >
                {getInitials(user.user_metadata?.full_name)}
              </div>
            </div>
            <div className="col">
              <h4 className="card-title mb-1">
                {user.user_metadata?.full_name || 'Usuario'}
              </h4>
              <p className="card-text mb-2">{user.email}</p>
              <p className="text-muted small mb-0">
                Miembro desde {formatDate(user.created_at)}
              </p>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary" onClick={openPasswordModal}>
                Cambiar Contraseña
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Account Information */}
      <div className="row">
        <div className="col-md-6">
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">Información de la Cuenta</h5>
            </div>
            <div className="card-body">
              <p>
                <strong>ID:</strong> {user.id}
              </p>
              <p>
                <strong>Rol:</strong> {user.role}
              </p>
              <p>
                <strong>Audiencia:</strong> {user.aud}
              </p>
              <p>
                <strong>Correo Confirmado:</strong>{' '}
                {formatDate(user.email_confirmed_at)}
              </p>
              <p>
                <strong>Último Inicio de Sesión:</strong>{' '}
                {formatDate(user.last_sign_in_at)}
              </p>
              <p>
                <strong>Actualizado:</strong> {formatDate(user.updated_at)}
              </p>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">Metadatos</h5>
            </div>
            <div className="card-body">
              <p>
                <strong>Nombre Completo:</strong>{' '}
                {user.user_metadata?.full_name}
              </p>
              <p>
                <strong>Correo Verificado:</strong>{' '}
                {user.user_metadata?.email_verified ? 'Sí' : 'No'}
              </p>
              <p>
                <strong>Requiere Cambio de Contraseña:</strong>{' '}
                {user.user_metadata?.requires_password_change ? 'Sí' : 'No'}
              </p>
              <p>
                <strong>Proveedor:</strong> {user.app_metadata?.provider}
              </p>
              <p>
                <strong>Proveedores:</strong>{' '}
                {user.app_metadata?.providers?.join(', ')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className="card my-2">
        <div className="card-header">
          <h5 className="card-title mb-0">Información Adicional</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-sm-6">
              <p>
                <strong>Teléfono:</strong> {user.phone || 'No proporcionado'}
              </p>
              <p>
                <strong>Confirmado:</strong> {formatDate(user.confirmed_at)}
              </p>
            </div>
            <div className="col-sm-6">
              <p>
                <strong>Recuperación Enviada:</strong>{' '}
                {user.recovery_sent_at
                  ? formatDate(user.recovery_sent_at)
                  : 'N/A'}
              </p>
              <p>
                <strong>Es Anónimo:</strong> {user.is_anonymous ? 'Sí' : 'No'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Identities */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="card-title mb-0">Identidades</h5>
        </div>
        <div className="card-body">
          {user.identities && user.identities.length > 0 ? (
            <div className="row">
              {user.identities.map((identity, index) => (
                <div key={index} className="col-md-6 mb-3">
                  <div className="border rounded p-3">
                    <p>
                      <strong>Proveedor:</strong> {identity.provider}
                    </p>
                    <p>
                      <strong>Correo:</strong> {identity.email}
                    </p>
                    <p>
                      <strong>Creado:</strong> {formatDate(identity.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No hay identidades disponibles.</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default MyAccount
